import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth } from '../firebase';
import type { Firestore } from 'firebase-admin/firestore';

export interface TenantSettings {
  tenantId: string;
  allocationMethod: string;
  payoutMethod: string;
  allowPayoutOverride?: boolean;
  payCycle: string;
  payDay: string;
  payDayDue: string;
  customFormula?: string;
  posTipHandling: 'Unpaid' | 'Reported';
  payrollProvider: string;
  posMerchantProvider?: string;
  adpCompanyCode?: string;
  displayTipBreakdown: boolean;
  timezone?: string;
  usePosShiftDataForActuals?: boolean;
  isPosTipAllocationRequired?: boolean;
  requireIRS8PercentCompliance?: boolean;
  irs8027Cadence?: 'pay_cycle' | 'monthly' | 'quarterly' | 'annually';
  businessIndustry?: string;
  employeeCountOver10?: boolean;
  filedForm8027LastYear?: boolean;
  customComplianceRate?: number;
  anchorDate?: string;
  // Service Charges & Auto-Gratuities
  enableServiceChargeDistribution?: boolean;
  serviceChargeEmployeeShare?: number; // e.g. 100 for 100%, 80 for 80%
  serviceChargeAllocationMethod?: 'direct_server' | 'shift_pool';
  // Credit Card Merchant Processing Fees
  enableMerchantFeeDeduction?: boolean;
  merchantFeeRate?: number; // e.g. 2.25 for 2.25%
  posMapping?: {
    staffName?: string;
    email?: string;
    tipDate?: string;
    amount?: string;
    posId?: string;
    shiftName?: string;
    startTime?: string;
    endTime?: string;
    hours?: string;
    serviceCharge?: string;
  };
}

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
    },
    operationType,
    path
  }
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

export const getSettings = async (tenantId: string): Promise<TenantSettings | null> => {
  const settingsRef = doc(db, 'tenants', tenantId, 'settings', 'config');
  try {
    const docSnap = await getDoc(settingsRef);
    if (docSnap.exists()) {
      return docSnap.data() as TenantSettings;
    }
    return null;
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, 'tenants/' + tenantId + '/settings/config');
    return null;
  }
};

export const getSettingsAdmin = async (tenantId: string, dbInstance: Firestore): Promise<TenantSettings | null> => {
  try {
    const docSnap = await dbInstance.collection('tenants').doc(tenantId).collection('settings').doc('config').get();
    if (docSnap.exists) {
      return docSnap.data() as TenantSettings;
    }
    return null;
  } catch (error: any) {
    if (error?.code === 7 || error?.message?.includes('PERMISSION_DENIED')) {
      console.warn(`[getSettingsAdmin] Admin Firestore permission not configured for tenant "${tenantId}". Skipping background sync.`);
    } else {
      console.error('Error fetching settings (admin):', error);
    }
    return null;
  }
};

export const saveSettings = async (tenantId: string, settings: TenantSettings) => {
  const settingsRef = doc(db, 'tenants', tenantId, 'settings', 'config');
  console.log('Attempting to save settings to:', settingsRef.path, 'Data:', settings);
  try {
    await setDoc(settingsRef, settings, { merge: true });
    console.log('Settings saved successfully');
  } catch (error) {
    console.error('Error saving settings:', error);
    handleFirestoreError(error, OperationType.WRITE, 'tenants/' + tenantId + '/settings/config');
  }
};
